import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Input } from '../ui/Input.js';
import { Select } from '../ui/Select.js';
import { Button } from '../ui/Button.js';
import { useToast } from '../ui/Toast.js';
import { useCreateContact } from '../../hooks/useContacts.js';

interface ContactFormState {
  firstName: string;
  lastName: string;
  businessName: string;
  primaryEmail: string;
  primaryPhone: string;
  category: string;
}

const EMPTY: ContactFormState = {
  firstName: '',
  lastName: '',
  businessName: '',
  primaryEmail: '',
  primaryPhone: '',
  category: 'client',
};

interface Props {
  onSuccess: (contact: { id: number; name: string }) => void;
  onCancel: () => void;
}

export function CreateContactForm({ onSuccess, onCancel }: Props) {
  const { t } = useTranslation();
  const toast = useToast();
  const createContact = useCreateContact();
  const [form, setForm] = useState<ContactFormState>(EMPTY);
  const [submitting, setSubmitting] = useState(false);

  const categoryOptions = [
    { value: 'client', label: t('contacts.category.client', { defaultValue: 'Client' }) },
    { value: 'prospect', label: t('contacts.category.prospect', { defaultValue: 'Prospect' }) },
    { value: 'personal', label: t('contacts.category.personal', { defaultValue: 'Personal' }) },
  ];

  async function handleSubmit() {
    setSubmitting(true);
    try {
      const result = await createContact.mutateAsync({
        firstName: form.firstName || null,
        lastName: form.lastName || null,
        businessName: form.businessName || null,
        primaryEmail: form.primaryEmail || null,
        primaryPhone: form.primaryPhone || null,
        category: form.category,
      });
      const name =
        [result.firstName, result.lastName].filter(Boolean).join(' ') ||
        result.businessName ||
        result.primaryEmail ||
        String(result.id);
      toast.show({ message: t('contacts.create.success'), tone: 'success' });
      onSuccess({ id: result.id, name });
    } catch {
      toast.show({ message: t('common.error'), tone: 'error' });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
        <Input
          label={t('contacts.create.firstName')}
          value={form.firstName}
          onChange={v => setForm(p => ({ ...p, firstName: v }))}
          fullWidth
        />
        <Input
          label={t('contacts.create.lastName')}
          value={form.lastName}
          onChange={v => setForm(p => ({ ...p, lastName: v }))}
          fullWidth
        />
      </div>
      <Input
        label={t('contacts.create.businessName')}
        value={form.businessName}
        onChange={v => setForm(p => ({ ...p, businessName: v }))}
        fullWidth
      />
      <Input
        label={t('contacts.create.primaryEmail')}
        type="email"
        value={form.primaryEmail}
        onChange={v => setForm(p => ({ ...p, primaryEmail: v }))}
        fullWidth
      />
      <Input
        label={t('contacts.create.primaryPhone')}
        type="tel"
        value={form.primaryPhone}
        onChange={v => setForm(p => ({ ...p, primaryPhone: v }))}
        fullWidth
      />
      <Select
        label={t('contacts.create.category')}
        value={form.category}
        onChange={v => { if (v) setForm(p => ({ ...p, category: v })); }}
        options={categoryOptions}
        fullWidth
      />
      <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
        <Button
          variant="ghost"
          tone="muted"
          size="sm"
          onClick={onCancel}
          disabled={submitting}
        >
          {t('contacts.create.cancel')}
        </Button>
        <Button
          size="sm"
          tone="cyan"
          onClick={() => { void handleSubmit(); }}
          loading={submitting}
        >
          {t('contacts.create.submit')}
        </Button>
      </div>
    </div>
  );
}
